/* nx-zoom — 弹层图片缩放（尺码表等），落地页 nx-lp-shop 与详情页 nx-pdp-main 共用。
 * 页面 viewport 设了 user-scalable=no，安卓上无法双指缩放整页，所以在 [data-nx-zoom] 滚动盒里自己缩放：
 * - 双指捏合：1–3 倍，以两指中点为锚点；
 * - 双击（鼠标）/ 双点（触屏）：在 1 倍和 2 倍之间切换，以点击处为锚点；
 * - 放大后单指 / 滚轮走盒子自身的原生滚动来平移（盒子 CSS：overflow auto + touch-action pan-x pan-y）。
 * 缩放值写在盒子的 --nx-zoom 上，盒内图片宽度 = 100% × --nx-zoom；弹层打开时由调用方调用 window.nxZoomReset(box) 复位。
 * 事件委托挂在 document 上，同一页多处调用只初始化一次。
 */
(() => {
  if (window.nxZoomReset) return;

  const MIN = 1;
  const MAX = 3;
  const TAP_MS = 300;
  const TAP_SLOP = 30;

  const clamp = (value) => Math.min(MAX, Math.max(MIN, value));
  const scaleOf = (box) => parseFloat(box.style.getPropertyValue('--nx-zoom')) || 1;
  const boxOf = (event) => event.target.closest?.('[data-nx-zoom]');
  const distance = ([a, b]) => Math.hypot(a.x - b.x, a.y - b.y);
  const midpoint = ([a, b]) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  // 以视口坐标 (x, y) 为锚点缩放：锚点下的内容缩放前后不动
  const zoomTo = (box, next, x, y) => {
    const prev = scaleOf(box);
    const scale = clamp(next);
    if (scale === prev) return;
    const rect = box.getBoundingClientRect();
    const offsetX = x - rect.left;
    const offsetY = y - rect.top;
    const contentX = box.scrollLeft + offsetX;
    const contentY = box.scrollTop + offsetY;
    box.style.setProperty('--nx-zoom', String(scale));
    box.classList.toggle('is-zoomed', scale > 1);
    box.scrollLeft = contentX * (scale / prev) - offsetX;
    box.scrollTop = contentY * (scale / prev) - offsetY;
  };

  const toggleAt = (box, x, y) => zoomTo(box, scaleOf(box) > 1 ? 1 : 2, x, y);

  const pointers = new Map();
  let pinch = null;
  let lastTap = { time: 0, x: 0, y: 0 };

  document.addEventListener('pointerdown', (event) => {
    const box = boxOf(event);
    if (!box) return;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, box });
    if (pointers.size === 2) {
      const points = [...pointers.values()];
      pinch = { box, distance: distance(points), scale: scaleOf(box) };
    }
  });

  document.addEventListener('pointermove', (event) => {
    const point = pointers.get(event.pointerId);
    if (!point) return;
    pointers.set(event.pointerId, { ...point, x: event.clientX, y: event.clientY });
    if (!pinch || pointers.size < 2 || !pinch.distance) return;
    const points = [...pointers.values()];
    const center = midpoint(points);
    zoomTo(pinch.box, (pinch.scale * distance(points)) / pinch.distance, center.x, center.y);
  });

  const release = (event) => {
    const point = pointers.get(event.pointerId);
    pointers.delete(event.pointerId);
    if (pointers.size < 2) pinch = null;
    if (!point || event.type !== 'pointerup' || event.pointerType === 'mouse') return;
    // 触屏双点：两次轻点间隔 < 300ms 且落点相近
    const now = performance.now();
    const near = Math.hypot(event.clientX - lastTap.x, event.clientY - lastTap.y) < TAP_SLOP;
    if (now - lastTap.time < TAP_MS && near) {
      toggleAt(point.box, event.clientX, event.clientY);
      lastTap = { time: 0, x: 0, y: 0 };
    } else {
      lastTap = { time: now, x: event.clientX, y: event.clientY };
    }
  };
  document.addEventListener('pointerup', release);
  document.addEventListener('pointercancel', release);

  document.addEventListener('dblclick', (event) => {
    const box = boxOf(event);
    box && toggleAt(box, event.clientX, event.clientY);
  });

  window.nxZoomReset = (box) => {
    if (!box) return;
    box.style.removeProperty('--nx-zoom');
    box.classList.remove('is-zoomed');
    box.scrollTop = 0;
    box.scrollLeft = 0;
  };
})();
